import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Cellar, UserProfile } from '../types';

interface ProfileModalProps {
  user: User;
  cellars: Cellar[];
  onClose: () => void;
  onProfileUpdated: () => void;
  onDeleteAccount: () => void;
  onSignOut: () => void;
}

export function ProfileModal({ user, cellars, onClose, onProfileUpdated, onDeleteAccount, onSignOut }: ProfileModalProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [username, setUsername] = useState('');
  const [defaultCellarId, setDefaultCellarId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    loadProfile();
  }, [user.id]);

  async function loadProfile() {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProfile(data);
        setUsername(data.username || '');
        setDefaultCellarId(data.default_cellar_id || '');
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  }

  function validateUsername(value: string): string | null {
    if (!value) return null; // Empty is OK (optional)
    
    if (value.length < 3) return 'Username must be at least 3 characters';
    if (value.length > 20) return 'Username must be 20 characters or less';
    if (!/^[a-zA-Z]/.test(value)) return 'Username must start with a letter';
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(value)) return 'Only letters, numbers, and underscores allowed';
    
    return null;
  }

  function handleUsernameChange(value: string) {
    setUsername(value);
    setUsernameError(validateUsername(value));
  }

  async function handleSave() {
    setError(null);
    
    // Validate username
    const validationError = validateUsername(username);
    if (validationError) {
      setUsernameError(validationError);
      return;
    }

    setSaving(true);

    try {
      // Check if username is taken (if changed)
      if (username && username.toLowerCase() !== (profile?.username || '').toLowerCase()) {
        const { data: available } = await supabase
          .rpc('is_username_available', { uname: username });
        
        if (!available) {
          setUsernameError('Username is already taken');
          setSaving(false);
          return;
        }
      }

      const profileData = {
        user_id: user.id,
        username: username.toLowerCase() || null,
        default_cellar_id: defaultCellarId || null,
        updated_at: new Date().toISOString()
      };

      if (profile) {
        // Update existing profile
        const { error } = await supabase
          .from('user_profiles')
          .update(profileData)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Create new profile
        const { error } = await supabase
          .from('user_profiles')
          .insert([profileData]);

        if (error) throw error;
      }

      onProfileUpdated();
      onClose();
    } catch (err: any) {
      console.error('Error saving profile:', err);
      if (err.message?.includes('valid_username')) {
        setUsernameError('Invalid username format');
      } else if (err.message?.includes('unique') || err.code === '23505') {
        setUsernameError('Username is already taken');
      } else {
        setError(err.message || 'Error saving profile');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div 
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ background: '#faf8f4' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: '#e8e4da' }}>
          <h2 className="text-xl wine-title" style={{ color: '#5d4e37' }}>Profile</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-lg"
            style={{ background: '#e8e4da', color: '#5d4e37' }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {loading ? (
            <div className="text-center py-8" style={{ color: '#8b7d6b' }}>Loading...</div>
          ) : (
            <>
              {/* Email (read-only) */}
              <div>
                <label className="block text-sm mb-1" style={{ color: '#5d4e37' }}>Email</label>
                <input
                  type="email"
                  value={user.email || ''}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border text-base"
                  style={{ borderColor: '#e8e4da', background: '#f5f1e8', color: '#8b7d6b' }}
                />
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm mb-1" style={{ color: '#5d4e37' }}>Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="Choose a username"
                  className="w-full px-4 py-3 rounded-lg border text-base"
                  style={{ 
                    borderColor: usernameError ? '#dc2626' : '#c9b998', 
                    background: 'white' 
                  }}
                />
                {usernameError ? (
                  <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{usernameError}</p>
                ) : (
                  <p className="text-xs mt-1" style={{ color: '#8b7d6b' }}>
                    3-20 characters, letters, numbers, underscores. Shown to cellar members.
                  </p>
                )}
              </div>

              {/* Default Cellar */}
              <div>
                <label className="block text-sm mb-1" style={{ color: '#5d4e37' }}>Default Cellar</label>
                <select
                  value={defaultCellarId}
                  onChange={(e) => setDefaultCellarId(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border text-base appearance-none"
                  style={{ borderColor: '#c9b998', background: 'white', color: '#5d4e37' }}
                >
                  <option value="">None (show cellar list)</option>
                  {cellars.map(cellar => (
                    <option key={cellar.id} value={cellar.id}>{cellar.name}</option>
                  ))}
                </select>
                <p className="text-xs mt-1" style={{ color: '#8b7d6b' }}>
                  Opens automatically when you log in
                </p>
              </div>

              {error && (
                <p className="text-sm" style={{ color: '#dc2626' }}>{error}</p>
              )}

              {/* Danger Zone */}
              <div className="pt-4 mt-4 border-t" style={{ borderColor: '#e8e4da' }}>
                <h3 className="text-sm font-medium mb-2" style={{ color: '#dc2626' }}>Danger Zone</h3>
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-sm"
                    style={{ color: '#dc2626' }}
                  >
                    Delete my account...
                  </button>
                ) : (
                  <div className="space-y-3 p-3 rounded-lg" style={{ background: '#fef2f2' }}>
                    <p className="text-sm" style={{ color: '#991b1b' }}>
                      This will permanently delete your account and remove you from all cellars. Type <strong>DELETE</strong> to confirm.
                    </p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      className="w-full px-3 py-2 rounded border text-sm"
                      style={{ borderColor: '#fca5a5' }}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                        className="flex-1 py-2 rounded text-sm"
                        style={{ background: '#e8e4da', color: '#5d4e37' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={onDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE'}
                        className="flex-1 py-2 rounded text-sm text-white disabled:opacity-50"
                        style={{ background: '#dc2626' }}
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t space-y-3" style={{ borderColor: '#e8e4da' }}>
          <button
            onClick={handleSave}
            disabled={saving || loading || !!usernameError}
            className="w-full py-3 rounded-lg text-white font-medium disabled:opacity-50"
            style={{ background: '#722f37' }}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          <button
            onClick={onSignOut}
            className="w-full py-3 rounded-lg font-medium"
            style={{ background: '#e8e4da', color: '#5d4e37' }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}
